import {
    BanknotesIcon,
    CheckCircleIcon,
    PencilIcon,
    PlusIcon,
    TrashIcon,
    XCircleIcon
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../../services/api';

const PropertyOwnerAccounts = () => {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedPropertyOwner, setSelectedPropertyOwner] = useState('');

  // 임대사업자 목록 조회
  const { data: propertyOwners } = useQuery({
    queryKey: ['property-owners'],
    queryFn: () => api.get('/property-owners')
  });

  // 임대사업자별 계좌 목록 조회
  const { data: accounts, isLoading: accountsLoading } = useQuery({
    queryKey: ['property-owner-accounts', selectedPropertyOwner],
    queryFn: () => {
      const params = new URLSearchParams();
      if (selectedPropertyOwner) {
        params.append('property_owner_id', selectedPropertyOwner);
      }
      const queryString = params.toString();
      const url = `/property-owner-accounts${queryString ? `?${queryString}` : ''}`;
      return api.get(url);
    },
    enabled: !!selectedPropertyOwner
  });

  // 계좌 통계 조회
  const { data: accountSummary } = useQuery({
    queryKey: ['property-owner-accounts-summary'],
    queryFn: () => api.get('/property-owner-accounts/summary')
  });

  // 계좌 생성 뮤테이션
  const createAccountMutation = useMutation({
    mutationFn: (accountData) => api.post('/property-owner-accounts', accountData),
    onSuccess: () => {
      queryClient.invalidateQueries(['property-owner-accounts']);
      queryClient.invalidateQueries(['property-owner-accounts-summary']);
      setShowCreateModal(false);
    }
  });

  // 계좌 수정 뮤테이션
  const updateAccountMutation = useMutation({
    mutationFn: ({ accountId, accountData }) => api.put(`/property-owner-accounts/${accountId}`, accountData),
    onSuccess: () => {
      queryClient.invalidateQueries(['property-owner-accounts']);
      queryClient.invalidateQueries(['property-owner-accounts-summary']);
      setShowEditModal(false);
      setSelectedAccount(null);
    }
  });

  // 계좌 삭제 뮤테이션
  const deleteAccountMutation = useMutation({
    mutationFn: (accountId) => api.delete(`/property-owner-accounts/${accountId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['property-owner-accounts']);
      queryClient.invalidateQueries(['property-owner-accounts-summary']);
    }
  });

  const handleCreateAccount = (accountData) => {
    createAccountMutation.mutate(accountData);
  };

  const handleEditAccount = (accountData) => {
    updateAccountMutation.mutate({
      accountId: selectedAccount.id,
      accountData
    });
  };

  const handleDeleteAccount = (accountId) => {
    if (window.confirm('정말로 이 계좌를 삭제하시겠습니까?')) {
      deleteAccountMutation.mutate(accountId);
    }
  };

  const getAccountTypeLabel = (type) => {
    const labels = {
      'rental': '월세',
      'deposit': '보증금',
      'maintenance': '관리비',
      'general': '일반'
    };
    return labels[type] || type;
  };

  const getAccountTypeColor = (type) => {
    const colors = {
      'rental': 'bg-blue-100 text-blue-800',
      'deposit': 'bg-green-100 text-green-800',
      'maintenance': 'bg-yellow-100 text-yellow-800',
      'general': 'bg-gray-100 text-gray-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">임대사업자 계좌 관리</h1>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              계좌 추가
            </button>
          </div>

          {/* 임대사업자 선택 */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              임대사업자 선택
            </label>
            <select
              value={selectedPropertyOwner}
              onChange={(e) => setSelectedPropertyOwner(e.target.value)}
              className="w-full max-w-md border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">임대사업자를 선택하세요</option>
              {propertyOwners?.map((owner) => (
                <option key={owner.id} value={owner.id}>
                  {owner.company_name || owner.name}
                </option>
              ))}
            </select>
          </div>

          {/* 계좌 통계 */}
          {accountSummary && accountSummary.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {accountSummary.map((summary) => (
                <div key={summary.property_owner_id} className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <BanknotesIcon className="h-6 w-6 text-blue-500" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            {summary.property_owner_name}
                          </dt>
                          <dd className="text-lg font-medium text-gray-900">
                            총 {summary.total_accounts}개 계좌
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-5 py-3">
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-500">활성 계좌</span>
                        <span className="text-green-600">{summary.active_accounts}개</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">월세 계좌</span>
                        <span className="text-blue-600">{summary.rental_accounts}개</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">보증금 계좌</span>
                        <span className="text-green-600">{summary.deposit_accounts}개</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 계좌 목록 */}
          {selectedPropertyOwner && (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  계좌 목록
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  선택된 임대사업자의 계좌 목록입니다.
                </p>
              </div>
              
              {accountsLoading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : accounts && accounts.length > 0 ? (
                <ul className="divide-y divide-gray-200">
                  {accounts.map((account) => (
                    <li key={account.id} className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-sm font-bold">
                                {account.bank_name?.charAt(0) || '?'}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center space-x-2">
                              <p className="text-sm font-medium text-gray-900">
                                {account.account_name}
                              </p>
                              {account.is_primary && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  주계좌
                                </span>
                              )}
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAccountTypeColor(account.account_type)}`}>
                                {getAccountTypeLabel(account.account_type)}
                              </span>
                            </div>
                            <div className="mt-1">
                              <p className="text-sm text-gray-500">
                                {account.bank_name} {account.account_number}
                              </p>
                              <p className="text-sm text-gray-500">
                                예금주: {account.account_holder}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="flex items-center space-x-1">
                            {account.is_active ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-red-500" />
                            )}
                            <span className="text-sm text-gray-500">
                              {account.is_active ? '활성' : '비활성'}
                            </span>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedAccount(account);
                              setShowEditModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteAccount(account.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {account.description && (
                        <div className="mt-2">
                          <p className="text-sm text-gray-600">{account.description}</p>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center py-12">
                  <BanknotesIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">등록된 계좌가 없습니다</h3>
                  <p className="mt-1 text-sm text-gray-500">새로운 계좌를 추가해보세요.</p>
                </div>
              )}
            </div>
          )}

          {/* 계좌 생성 모달 */}
          {showCreateModal && (
            <AccountModal
              propertyOwners={propertyOwners || []}
              onClose={() => setShowCreateModal(false)}
              onSubmit={handleCreateAccount}
              loading={createAccountMutation.isPending}
            />
          )}

          {/* 계좌 수정 모달 */}
          {showEditModal && selectedAccount && (
            <AccountModal
              propertyOwners={propertyOwners || []}
              account={selectedAccount}
              onClose={() => {
                setShowEditModal(false);
                setSelectedAccount(null);
              }}
              onSubmit={handleEditAccount}
              loading={updateAccountMutation.isPending}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// 계좌 모달 컴포넌트
const AccountModal = ({ propertyOwners, account, onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    property_owner_id: account?.property_owner_id || '',
    account_name: account?.account_name || '',
    bank_name: account?.bank_name || '',
    account_number: account?.account_number || '',
    account_holder: account?.account_holder || '',
    account_type: account?.account_type || 'rental',
    is_primary: account?.is_primary || false,
    is_active: account?.is_active !== false,
    auto_matching_enabled: account?.auto_matching_enabled !== false,
    sms_notification_enabled: account?.sms_notification_enabled !== false,
    description: account?.description || '',
    notes: account?.notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {account ? '계좌 수정' : '계좌 추가'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">임대사업자</label>
              <select
                value={formData.property_owner_id}
                onChange={(e) => setFormData({...formData, property_owner_id: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
                disabled={!!account}
              >
                <option value="">임대사업자를 선택하세요</option>
                {propertyOwners.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.company_name || owner.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">계좌명</label>
              <input
                type="text"
                value={formData.account_name}
                onChange={(e) => setFormData({...formData, account_name: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="예: 월세수납계좌"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">은행명</label>
              <input
                type="text"
                value={formData.bank_name}
                onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="예: 국민은행"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">계좌번호</label>
              <input
                type="text"
                value={formData.account_number}
                onChange={(e) => setFormData({...formData, account_number: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="예: 123-456-789"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">예금주</label>
              <input
                type="text"
                value={formData.account_holder}
                onChange={(e) => setFormData({...formData, account_holder: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">계좌 유형</label>
              <select
                value={formData.account_type}
                onChange={(e) => setFormData({...formData, account_type: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="rental">월세</option>
                <option value="deposit">보증금</option>
                <option value="maintenance">관리비</option>
                <option value="general">일반</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_primary}
                  onChange={(e) => setFormData({...formData, is_primary: e.target.checked})}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">주계좌</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">활성화</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.auto_matching_enabled}
                  onChange={(e) => setFormData({...formData, auto_matching_enabled: e.target.checked})}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">자동매칭 활성화</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.sms_notification_enabled}
                  onChange={(e) => setFormData({...formData, sms_notification_enabled: e.target.checked})}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">SMS 알림 활성화</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">설명</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                rows={2}
                placeholder="계좌에 대한 설명을 입력하세요"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">메모</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                rows={2}
                placeholder="추가 메모를 입력하세요"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? '처리 중...' : (account ? '수정' : '추가')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PropertyOwnerAccounts;

